import React from 'react';
import { Button } from './ui/button';
import { Camera, Zap, TrendingUp } from 'lucide-react';
import { stats } from '../mock';
import { captureEvent } from '../utils/posthog';
import { EVENTS, PROPERTIES } from '../utils/analytics';

export const Hero = () => {
  const scrollToWaitlist = () => {
    const element = document.getElementById('waitlist');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      captureEvent(EVENTS.CTA_BUTTON_CLICKED, {
        [PROPERTIES.CTA_LOCATION]: 'hero',
        [PROPERTIES.CTA_TEXT]: 'Get Early Access',
        [PROPERTIES.NAVIGATION_TARGET]: 'waitlist',
      });
    }
  };

  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      captureEvent(EVENTS.CTA_BUTTON_CLICKED, {
        [PROPERTIES.CTA_LOCATION]: 'hero',
        [PROPERTIES.CTA_TEXT]: 'See How It Works',
        [PROPERTIES.NAVIGATION_TARGET]: 'how-it-works',
      });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="inline-block">
              <div className="flex items-center space-x-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-500 text-sm font-medium">Launch Special - Early Access</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Upload a photo,<br />
              <span className="text-gradient">Get instant listings</span>
            </h1>

            <p className="text-xl text-gray-400 leading-relaxed">
              Fetch images, descriptions, pricing & reviews — then publish directly to Amazon, Flipkart, or Shopify. Build your product catalogue <span className="text-yellow-500 font-semibold">10× faster</span>.
            </p>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 glass rounded-lg p-4">
                <Camera className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-white font-semibold">Instant Scan</p>
                  <p className="text-gray-400 text-sm">AI Recognition</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 glass rounded-lg p-4">
                <Zap className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-white font-semibold">Auto-Fetch</p>
                  <p className="text-gray-400 text-sm">All Details</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 glass rounded-lg p-4">
                <TrendingUp className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-white font-semibold">One-Click</p>
                  <p className="text-gray-400 text-sm">Publishing</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToWaitlist}
                size="lg"
                className="btn-primary bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 pulse-glow"
              >
                Get Early Access
              </Button>
              <Button
                onClick={scrollToHowItWorks}
                size="lg"
                variant="outline"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black font-semibold text-lg px-8 py-6 transition-colors duration-300"
              >
                See How It Works
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-gray-800">
              {stats.map((stat, index) => (
                <div key={index} className="text-center sm:text-left">
                  <p className="text-3xl font-bold text-yellow-500">{stat.value}</p>
                  <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative animate-fade-in-up stagger-2">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1661870139279-95fecab7c53a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxvbmxpbmUlMjBzZWxsaW5nfGVufDB8fHx8MTc2NTMwNjkzOHww&ixlib=rb-4.1.0&q=85"
                alt="Online selling with Snapcom"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 glass rounded-2xl p-6">
                <p className="text-yellow-500 font-bold text-2xl">500K+</p>
                <p className="text-white text-sm">Products Listed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};